import type { ColorName } from '@emotion/react';
import { getReferenceId } from '@quromedical/fhir-common';
import {
  type CreateMediaData,
  type DeepPartial,
  MeasurementName,
  type Note,
  type PartialStructuredContent,
  type StructuredNoteItem,
  type StructuredNoteNode,
  type TaskGetResponse,
  type TaskUpdate,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { MarkdownRenderer } from '@quromedical/utils/ui';
import { getNotePlanDisplay } from 'core/display';
import {
  type UpdateTask as AddTaskNote,
  type AssignmentResult,
  TaskItem,
} from 'core/task';
import { getStatusMeasurements } from 'core/vitals';
import {
  NoteCard as Base,
  CommentDisplay,
  type Measurement,
  type NestedComment,
  type NoteAction,
  type Note as NoteDesignSystem,
  type NoteEdit,
  type TagConfig,
} from 'design-system/components';
import { usePatient } from 'hooks/usePatient';
import { usePractitionerByOrgFetcher } from 'hooks/usePractitioners';
import type React from 'react';
import { useMemo } from 'react';

import { renderers } from './helpers';
import { codeAssessment, codeDisplay, codeLocation, codePlan } from './record';

export { codeAssessment, codeDisplay, codeLocation, codePlan } from './record';

export interface NoteWithTask extends Note {
  task?: TaskGetResponse;
}

type AddNoteFn = (input: TaskUpdate) => Promise<void>;
type OnTaskSubmitFn = (result?: AssignmentResult) => Promise<void>;

interface NoteCardProps {
  patientId: string;
  note: NoteWithTask;
  onAttachmentPress: (id: string) => Promise<void>;
  onTaskNoteAdd?: AddTaskNote;
  onPin?: () => void;
  onEditSubmit?: (
    subjective: string,
    media: CreateMediaData[],
  ) => Promise<void>;
  onTaskSubmit: OnTaskSubmitFn | undefined;
  canPin: boolean;
  canEdit: boolean;
  canCreateTask?: boolean;
  addNote: AddNoteFn | undefined;
}

type DeepPartialStructuredData = DeepPartial<StructuredNoteNode>;

const flattenComments = (
  node: DeepPartialStructuredData,
): Partial<StructuredNoteItem>[] => {
  if (!node) {
    return [];
  }

  if (node.type === 'comment') {
    return [node];
  }

  if (node.type === 'group') {
    if (!node.children?.length) {
      return [];
    }

    return compact(node.children).map(flattenComments).flat();
  }

  return [];
};

const mapNoteItemToComment = (
  note: Partial<StructuredNoteItem>,
): Partial<NestedComment.CommentWithPath> => ({
  ...note,
  color: note.color as ColorName,
});

const displayStructuredContent = (
  content?: PartialStructuredContent,
): NoteDesignSystem[] | undefined => {
  const notes = content?.data;

  if (!notes?.length) {
    return undefined;
  }

  return notes.map((note) => {
    const comments = flattenComments(note).map(mapNoteItemToComment);

    return {
      title: note.display,
      body:
        note.type === 'group' ? (
          <CommentDisplay comments={[comments]} />
        ) : undefined,
    };
  });
};

const isFullMeasure = (measure: Partial<Measurement>): measure is Measurement =>
  measure.value !== undefined;

export const NoteCard: React.FC<NoteCardProps> = ({
  patientId,
  note,
  onAttachmentPress,
  onPin,
  canPin,
  canEdit,
  onEditSubmit,
  onTaskSubmit,
  addNote,
}) => {
  const createdBy = note.assessor?.display;
  const date = note.dateCreated ? new Date(note.dateCreated) : undefined;
  const { organizations, isLoading } = usePatient(patientId);

  const orgs = useMemo(
    () => (!isLoading ? compact(organizations?.map(getReferenceId)) : []),
    [organizations, isLoading],
  );

  const { options } = usePractitionerByOrgFetcher(orgs);

  const statusMeasurements = getStatusMeasurements({
    latest: note.vitals,
    additionalMeasures: [MeasurementName.Posture],
  }).filter((v) => v.hasValue);

  const measurements = statusMeasurements
    .map<Partial<Measurement>>((measure) => ({
      value: measure.value,
      name: measure.name,
      unit: measure.unit,
    }))
    .filter(isFullMeasure);

  const attachments = note.media.map((attachment) => ({
    name: attachment.title || '',
    onDownloadPress: () => onAttachmentPress(attachment.id),
  }));

  const code: TagConfig | undefined = note.code && {
    color: 'accent-blue',
    text: codeDisplay[note.code],
  };

  const location: TagConfig | undefined = note.locationCode && {
    color: 'accent-green',
    text: codeLocation[note.locationCode],
  };

  const serviceDisplay = getNotePlanDisplay(
    note.dateCreated,
    note.admission?.service,
    note.admission?.period,
    note.admission?.statusHistory,
  );
  const service: TagConfig | undefined = serviceDisplay
    ? {
        color: 'accent-purple',
        text: serviceDisplay,
      }
    : undefined;

  const tags: TagConfig[] = compact([code, location, service]);

  const subjectNotes = note.note.subjective?.length
    ? note.note.subjective?.map((subjective) => ({
        body: subjective,
      }))
    : [];

  const notes: NoteDesignSystem[] = compact([
    ...subjectNotes,
    {
      body: note.note.objective,
      measurements,
    },
    {
      title:
        note.note.assessmentCode && codeAssessment[note.note.assessmentCode],
      body: note.note.assessment,
    },
    {
      title: note.note.planCode && codePlan[note.note.planCode],
      body: note.note.plan,
    },
    {
      body: (
        <MarkdownRenderer
          content={note.note.dailySummary || ''}
          renderers={renderers}
        />
      ),
    },
    {
      body: (
        <MarkdownRenderer
          content={note.note.overallSummary || ''}
          renderers={renderers}
        />
      ),
    },
  ]);

  const structuredNote = displayStructuredContent(note.structuredContent);

  const resolvedNote: NoteDesignSystem[] = structuredNote
    ? [
        {
          measurements,
        },
        ...structuredNote,
      ]
    : notes;

  const action: NoteAction | undefined =
    canPin && onPin
      ? {
          icon: note.pinned ? 'star' : 'star-outline',
          color: note.pinned ? 'accent-yellow' : undefined,
          onPress: onPin,
        }
      : undefined;

  const edit: NoteEdit | undefined =
    canEdit && onEditSubmit
      ? {
          canEdit,
          onEditSubmit,
        }
      : undefined;

  const mergedAttachments = useMemo(() => {
    if (!note.task) {
      return [...attachments];
    }

    const onNoteAdd = async (input: TaskUpdate) => {
      await addNote?.(input);
    };

    const tasks = [
      <TaskItem key={note.task.id} task={note.task} onNoteAdd={onNoteAdd} />,
    ];

    return [...attachments, ...tasks];
  }, [attachments, note.task, addNote]);

  const taskAssignment =
    onTaskSubmit && !note.task
      ? {
          onSubmit: onTaskSubmit,
          performers: options,
        }
      : undefined;

  return (
    <Base
      patientId={patientId}
      createdBy={createdBy}
      date={date}
      tags={tags}
      attachments={mergedAttachments}
      notes={resolvedNote}
      action={action}
      edit={edit}
      taskAssignment={taskAssignment}
    />
  );
};
